import { ReactElement, useCallback } from "react";
import { proxy, ref, useSnapshot } from "valtio";

import { useErrorHandling } from "./useErrorHandling";

interface IBottomMenuContextItem {
	/** Id is used to know the tree or replace a specific item  */
	id: string;

	/** The react node to show for this menu id */
	menuItem: ReactElement;
}

/** Load a new stack of bottom menu's to get to a specified path */
function loadBottomMenuPathAsync(
	currentStack: ReadonlyArray<IBottomMenuContextItem>,
	path: string,
	getMenuAsync: (menuId: string) => Promise<ReactElement | undefined>,
) {
	const pathParts = (path || "").split("/");

	// Find the common root index
	let commonRootIndex = -1;
	currentStack.every((menuItem, index) => {
		if (pathParts[index] !== menuItem.id) return false; // other path
		commonRootIndex = index;
		return true;
	});

	// Are we already on the right position?
	if (pathParts.length - 1 === commonRootIndex && currentStack.length - 1 === commonRootIndex) {
		return Promise.resolve(currentStack);
	}

	// Target is already available as parent?
	if (pathParts.length - 1 === commonRootIndex) {
		return Promise.resolve(currentStack.slice(0, commonRootIndex + 1));
	}

	// Re-use common root menus
	const newState = currentStack.slice(0, commonRootIndex + 1);

	// Can we load childs
	return pathParts
		.slice(commonRootIndex + 1) // remove parts from path already loaded
		.reduce(
			(promise, pathPart, newPathIndex) =>
				promise.then((stack) => {
					// Check if we need to stop
					if (newPathIndex >= stack.length - commonRootIndex) return stack;

					// Get Menu Component
					return getMenuAsync(pathPart).then((menuItem) => {
						if (menuItem) {
							stack.push({
								id: pathPart,
								menuItem,
							});
						}
						return stack;
					});
					// get next item
				}),
			Promise.resolve(newState),
		);
}

interface IBottomMenuState {
	/** Stack of menu items */
	stack: ReadonlyArray<IBottomMenuContextItem>;

	/** Last requested menu path that is loading (async) */
	loadingPath: string | undefined;

	/** Ignore new bottommenu changes */
	isEnabled: boolean;

	/** The current path (The menuId's separated with '/') */
	readonly currentPath: string | undefined;

	// Function to enable/disable menu
	setEnabled(isEnabled: boolean): void;

	// Set the function that creates menu's
	setComponentCreator(
		createMenuAsync: (menuId: string) => Promise<ReactElement | undefined>,
	): void;

	/** A function used to create menu items, needs to be set */
	createMenuAsync: ((menuId: string) => Promise<ReactElement | undefined>) | undefined;

	/** Navigate to a specific item, create items when needed */
	gotoPathAsync(pathData: string | undefined): Promise<void> | undefined;
}

/** Create a function, so unit tests can create a fresh instance */
export const createBottomMenuState = () => {
	const bottomMenuState = proxy<IBottomMenuState>({
		stack: [],
		loadingPath: undefined,
		isEnabled: true,
		createMenuAsync: undefined,

		/** Computed value from the stack */
		get currentPath(): string | undefined {
			return bottomMenuState.stack.length
				? bottomMenuState.stack.map((i) => i.id).join("/")
				: undefined;
		},

		setEnabled(isEnabled: boolean) {
			bottomMenuState.isEnabled = isEnabled;
		},

		setComponentCreator(createMenuAsync) {
			bottomMenuState.createMenuAsync = ref(createMenuAsync); // ref no tacking is needed
		},

		gotoPathAsync(path): Promise<void> | undefined {
			const state = bottomMenuState;
			if (!state.isEnabled) return undefined;

			// Shortcut: Remove all
			if (path === undefined) {
				state.stack = [];
				state.loadingPath = undefined; // New stack set, nothing loading
				return undefined;
			}

			if (!state.createMenuAsync) {
				throw new Error(
					"BottomMenu needs a function to create menu's based on an id. Set 'createMenuAsync'.",
				);
			}

			// Indicate that it is loading
			state.loadingPath = path;

			return loadBottomMenuPathAsync(
				state.stack, // Previous path
				path,
				state.createMenuAsync,
			)
				.then((newStack) => {
					if (!newStack) return;

					// Still update if menu is disabled after update request?
					// If already another menu is started to load during async load, cancel update
					if (state.loadingPath !== path) return;

					state.stack = newStack.map((item) => ({
						id: item.id,
						menuItem: ref(item.menuItem || undefined), // Don't proxy a ReactNode
					}));
					state.loadingPath = undefined; // Disable loading
				})
				.catch((err: unknown) => {
					// Still update if menu is disabled after update request?
					// If already another menu is started to load during async load, cancel update
					if (state.loadingPath !== path) return;

					state.loadingPath = undefined; // Disable loading

					throw err; // rethrow
				});
		},
	});
	return bottomMenuState;
};

const bottomMenuState = createBottomMenuState();

export function useBottomMenu() {
	const { createErrorHandler } = useErrorHandling();

	return {
		/** Get the data/methods from bottomMenuState */
		...useSnapshot<Readonly<Omit<IBottomMenuState, "createMenuAsync">>>(bottomMenuState), // Hide some members or make them readonly

		/** Wraps the gotoPathAsync with error handling */
		gotoPath: useCallback(
			(pathData: string | undefined): void => {
				bottomMenuState.gotoPathAsync(pathData)?.catch(createErrorHandler());
			},
			[createErrorHandler],
		),
	};
}
